import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Card, Input, Result, Space, message } from 'antd';
import {
  ArrowLeftOutlined,
  CloudUploadOutlined,
  CodeOutlined,
  CopyOutlined,
  EditOutlined,
  ExportOutlined,
} from '@ant-design/icons';
import { copyToClipboard } from '../../helpers/clipboard';
import { uploadHtmlContent } from '../../helpers/file';
import { useTranslation } from '../../hooks/useTranslation';
import { isValidHtmlFileName } from '../../utils/htmlFileName';
import { showUploadErrorModal } from '../../utils/uploadErrorModal';
import {
  WebHostingAiDraft,
  clearWebHostingAiDraft,
  getWebHostingAiDraft,
  saveWebHostingAiDraft,
} from '../../utils/webHostingAiDraft';
import './index.css';

interface UploadedRecord {
  id?: string | number;
  url?: string;
}

const GOOGLE_FONTS_CSS2_URL = 'https://fonts.googleapis.com/css2';

const WebHostingAiPreview: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [draft, setDraft] = useState<WebHostingAiDraft | null>(() => getWebHostingAiDraft());
  const [uploading, setUploading] = useState(false);
  const [uploadedRecord, setUploadedRecord] = useState<UploadedRecord | null>(null);

  const hasGoogleFonts = useMemo(
    () => Boolean(draft?.html.includes(GOOGLE_FONTS_CSS2_URL)),
    [draft?.html],
  );

  const updateFileName = (fileName: string) => {
    if (!draft) {
      return;
    }
    const nextDraft = { ...draft, fileName };
    setDraft(nextDraft);
    try {
      saveWebHostingAiDraft(nextDraft);
    } catch {
      message.error(t('webHostingAi.localDraftSaveFailed'));
    }
  };

  const handleUpload = async () => {
    if (!draft) {
      return;
    }
    const fileName = draft.fileName.trim();
    if (!isValidHtmlFileName(fileName)) {
      message.warning(t('webHosting.invalidFileName'));
      return;
    }
    setUploading(true);
    try {
      const record = await uploadHtmlContent(
        fileName,
        draft.html,
        (percent) => {
          const content = percent === 100
            ? t('upload.uploadComplete')
            : t('upload.uploadProgress', { percent });
          message.info({ key: 'webHostingAiUpload', duration: 1000, content });
        },
        (errorMessage, failedFileName, fileSize, httpStatus) => {
          showUploadErrorModal(t, errorMessage, failedFileName, undefined, fileSize, httpStatus);
        },
      );
      clearWebHostingAiDraft();
      setUploadedRecord(record || {});
      setDraft(null);
      message.success({ key: 'webHostingAiUpload', content: t('webHostingAi.uploadSuccess') });
    } catch (error) {
      // uploadHtmlContent already presents the detailed failure.
    } finally {
      setUploading(false);
    }
  };

  if (uploadedRecord) {
    return (
      <div className="web-hosting-ai-preview-page web-hosting-ai-preview-result">
        <Result
          status="success"
          title={t('webHostingAi.publishComplete')}
          subTitle={uploadedRecord.url}
          extra={[
            uploadedRecord.url ? (
              <Button
                key="open"
                icon={<ExportOutlined />}
                onClick={() => window.open(uploadedRecord.url, '_blank', 'noopener,noreferrer')}
              >
                {t('webHostingAi.openPage')}
              </Button>
            ) : null,
            uploadedRecord.url ? (
              <Button
                key="copy"
                icon={<CopyOutlined />}
                onClick={() => copyToClipboard(uploadedRecord.url || '', t('common.linkCopied'))}
              >
                {t('common.copyLink')}
              </Button>
            ) : null,
            uploadedRecord.id ? (
              <Button
                key="edit"
                type="primary"
                icon={<EditOutlined />}
                onClick={() => navigate(`/web-hosting/editor/${uploadedRecord.id}`)}
              >
                {t('webHostingAi.editPublished')}
              </Button>
            ) : null,
            <Button key="list" onClick={() => navigate('/web-hosting')}>
              {t('htmlVisualEditor.backToList')}
            </Button>,
          ].filter(Boolean)}
        />
      </div>
    );
  }

  if (!draft) {
    return (
      <div className="web-hosting-ai-preview-page web-hosting-ai-preview-result">
        <Result
          status="warning"
          title={t('webHostingAi.draftMissingTitle')}
          subTitle={t('webHostingAi.draftMissingDescription')}
          extra={(
            <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate('/web-hosting')}>
              {t('htmlVisualEditor.backToList')}
            </Button>
          )}
        />
      </div>
    );
  }

  return (
    <div className="web-hosting-ai-preview-page">
      <div className="web-hosting-ai-preview-hero">
        <Button type="link" icon={<ArrowLeftOutlined />} onClick={() => navigate('/web-hosting')}>
          {t('htmlVisualEditor.backToList')}
        </Button>
        <Space wrap>
          <Button
            icon={<CodeOutlined />}
            onClick={() => navigate('/web-hosting/editor/ai-draft')}
          >
            {t('webHostingAi.visualEdit')}
          </Button>
          <Button
            type="primary"
            icon={<CloudUploadOutlined />}
            loading={uploading}
            disabled={!isValidHtmlFileName(draft.fileName.trim())}
            onClick={handleUpload}
          >
            {t('webHostingAi.confirmUpload')}
          </Button>
        </Space>
      </div>

      {hasGoogleFonts && (
        <Alert
          message={t('webHosting.googleFontsWarningTitle')}
          description={t('webHosting.googleFontsWarningDescription')}
          type="warning"
          showIcon
        />
      )}

      <div className="web-hosting-ai-preview-file-name">
        <Input
          value={draft.fileName}
          onChange={(event) => updateFileName(event.target.value)}
          addonBefore={t('webHosting.fileName')}
          status={draft.fileName && !isValidHtmlFileName(draft.fileName.trim()) ? 'error' : undefined}
        />
      </div>

      <Card className="web-hosting-ai-preview-card" bodyStyle={{ padding: 0 }}>
        <iframe
          title={t('webHostingAi.previewFrameTitle')}
          className="web-hosting-ai-preview-frame"
          srcDoc={draft.html}
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
        />
      </Card>
    </div>
  );
};

export default WebHostingAiPreview;
